import React from "react";
import { jsPDF } from "jspdf";
import RiskItem from "./RiskItem";
import CompanyInfo from "./CompanyInfo";
import RiskGradeBar from "./RiskGradeBar";
import OverallRiskGradeBar from "./OverallRiskGradeBar";
import { formatDate, formatDateForPDF } from "../utils/dateUtils";
import analytics from "../services/analytics";

const ResultsSection = ({ result, company_data }) => {
  if (!result) return null; // Don't render if no result is available

  // Use data directly from backend result
  const {
    locationScoresMap,
    industryScoresMap,
    avgLocationRisk,
    avgIndustryRisk,
    overallScore,
    avgLocationRiskGrade,
    avgIndustryRiskGrade,
    overallScoreGrade,
  } = result;

  const friendlyDate = formatDate(result.created_at);
  const pdfDate = formatDateForPDF(result.created_at);

  // Helper function to convert numeric values to descriptive labels
  const getLikelihoodLabel = (value) => {
    switch(value) {
      case 0: return "Negligible";
      case 1: return "Very Low";
      case 2: return "Low";
      case 3: return "Moderate";
      case 4: return "High";
      case 5: return "Very High";
      default: return "Unknown";
    }
  };

  const getImpactLabel = (value) => {
    switch(value) {
      case 0: return "Negligible";
      case 1: return "Very Low";
      case 2: return "Low";
      case 3: return "Moderate";
      case 4: return "High";
      case 5: return "Very High";
      default: return "Unknown";
    }
  };

  // Function to generate PDF
  const generatePDF = () => {
    // Track PDF download attempt
    const reportId = result.id;
    const companyName = company_data.name || 'Unknown';
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    let yPos = 20; // Initial Y position
    const pageHeight = doc.internal.pageSize.height - 20; // Leave space for footer

    const addPageIfNeeded = (increment = 10) => {
      if (yPos + increment > pageHeight) {
        doc.addPage();
        yPos = 20; // Reset Y position for new page
      }
    };

    // Custom text wrapping function that preserves word spacing
    const wrapText = (text, maxWidth) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = '';
      
      for (let word of words) {
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const testWidth = doc.getTextWidth(testLine);
        
        if (testWidth > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
      
      if (currentLine) {
        lines.push(currentLine);
      }
      
      return lines;
    };

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Risk Assessment Report", 20, yPos);
    yPos += 10;

    // Report Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Report ID: ${result.id}`, 20, yPos);
    yPos += 6;
    doc.text(`Date: ${pdfDate}`, 20, yPos);
    yPos += 10;

    // Company Data Section
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Company Data", 20, yPos);
    yPos += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    const companyInfo = [
      `  Name: ${company_data.name || ""}`,
      `  Address: ${company_data.address || ""}`,
      `  Fiscal Number: ${company_data.fiscal_number || ""}`,
      `  Postal Code: ${company_data.postal_code || ""}`,
      `  Latitude: ${company_data.latitude || ""}`,
      `  Longitude: ${company_data.longitude || ""}`,
    ];
    companyInfo.forEach((info) => {
      addPageIfNeeded();
      doc.text(info, 20, yPos);
      yPos += 6;
    });

    // Risk Assessment - Overall Score Section
    addPageIfNeeded(16);
    doc.setFont("helvetica", "bold");
    yPos += 4;
    doc.setFontSize(16);
    doc.text("Risk Assessment", 20, yPos);
    yPos += 10;

    // Overall Risk Score - Prominent
    doc.setFontSize(14);
    doc.text("OVERALL RISK SCORE", 20, yPos);
    yPos += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`  Grade: [ ${overallScoreGrade} ] - Score: ${overallScore}/100`, 20, yPos);
    yPos += 6;
    const riskDescription = overallScoreGrade === 'A' ? 'Excellent - Very Low Risk' :
                           overallScoreGrade === 'B' ? 'Good - Low Risk' :
                           overallScoreGrade === 'C' ? 'Average - Moderate Risk' :
                           overallScoreGrade === 'D' ? 'Below Average - High Risk' :
                           'Poor - Very High Risk';
    doc.text(`  Risk Level: ${riskDescription}`, 20, yPos);
    yPos += 10;

    // Location Risks Section
    addPageIfNeeded(16);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Location-Based Risks", 20, yPos);
    yPos += 8;

    // Location Risk Average
    doc.setFontSize(12);
    doc.text(`  Average: [ ${avgLocationRiskGrade} ] ${avgLocationRisk}/100`, 20, yPos);
    yPos += 8;

    doc.setFont("helvetica", "normal");
    Object.entries(result.location_risk || {}).forEach(([risk, details]) => {
      addPageIfNeeded(20);

      // Format the title line with enhanced information
      const scoreData = locationScoresMap[risk];
      const title = `  • ${risk.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}`;
      
      doc.setFont("helvetica", "bold");
      doc.text(title, 20, yPos);
      yPos += 6;

      // Score and grade information
      doc.setFont("helvetica", "normal");
      doc.text(`    Grade: [ ${scoreData.grade} ] - Score: ${scoreData.score}/100`, 20, yPos);
      yPos += 5;
      doc.text(`    Likelihood: ${getLikelihoodLabel(scoreData?.rawLikelihood)} (${scoreData?.rawLikelihood}/5)`, 20, yPos);
      yPos += 5;
      doc.text(`    Impact: ${getImpactLabel(scoreData?.impact)} (${scoreData?.impact}/5)`, 20, yPos);
      yPos += 6;

      // Reasons as bullet points
      doc.text(`    Reasons:`, 20, yPos);
      yPos += 5;
      details.reason.forEach((reason) => {
        const wrappedText = wrapText(`      • ${reason}`, 160);
        wrappedText.forEach((line) => {
          addPageIfNeeded();
          doc.text(line, 20, yPos);
          yPos += 5;
        });
      });

      yPos += 4; // Extra space after each risk section
    });

    // Industry Risks Section
    addPageIfNeeded(16);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Industry-Specific Risks", 20, yPos);
    yPos += 8;

    // Industry Risk Average
    doc.setFontSize(12);
    doc.text(`  Average: [ ${avgIndustryRiskGrade} ] ${avgIndustryRisk}/100`, 20, yPos);
    yPos += 8;

    doc.setFont("helvetica", "normal");
    Object.entries(result.sector_specific_risk || {}).forEach(([risk, details]) => {
      addPageIfNeeded(20);

      // Format the title line with enhanced information
      const scoreData = industryScoresMap[risk];
      const title = `  • ${risk.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}`;
      
      doc.setFont("helvetica", "bold");
      doc.text(title, 20, yPos);
      yPos += 6;

      // Score and grade information
      doc.setFont("helvetica", "normal");
      doc.text(`    Grade: [ ${scoreData.grade} ] - Score: ${scoreData.score}/100`, 20, yPos);
      yPos += 5;
      doc.text(`    Likelihood: ${getLikelihoodLabel(scoreData?.rawLikelihood)} (${scoreData?.rawLikelihood}/5)`, 20, yPos);
      yPos += 5;
      doc.text(`    Impact: ${getImpactLabel(scoreData?.impact)} (${scoreData?.impact}/5)`, 20, yPos);
      yPos += 6;

      // Reasons as bullet points
      doc.text(`    Reasons:`, 20, yPos);
      yPos += 5;
      details.reason.forEach((reason) => {
        const wrappedText = wrapText(`      • ${reason}`, 160);
        wrappedText.forEach((line) => {
          addPageIfNeeded();
          doc.text(line, 20, yPos);
          yPos += 5;
        });
      });

      yPos += 4; // Extra space after each risk section
    });

    // Key Takeaways Section
    addPageIfNeeded(12);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Key Takeaways for Insurers", 20, yPos);
    yPos += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    result.key_takeaways_for_insurers?.forEach((takeaway) => {
      const wrappedText = wrapText(`  • ${takeaway}`, 160);
      wrappedText.forEach((line, index) => {
        addPageIfNeeded();
        doc.text(line, 20, yPos);
        yPos += 6;
      });
    });

    // Save the PDF
    const fileName = `Risk_Assessment_${
      company_data.name.replace(/\s+/g, "_") || "Report"
    }.pdf`;
    
    doc.save(fileName);
    
    // Track successful PDF download
    analytics.trackDownload('risk_assessment_pdf');
    analytics.event('Risk Assessment', 'report_downloaded', companyName, null, {
      report_id: reportId,
      file_name: fileName,
      company_name: companyName
    });
  };

  return (
    <div>
      <div className="mt-8 p-6 border rounded-lg bg-blue-50 font-['Archivo']">
        <h2 className="text-4xl font-bold text-blue-950">
          Your Risk Assessment Report
        </h2>
        <p className="text-blue-800">
          Here are the insights generated based on the information provided.
        </p>

        <div className="mt-3 font-bold flex flex-col">
          <div>
            Report ID: <span className="font-normal">{result.id}</span>
          </div>
          <div>
            Date: <span className="font-normal">{friendlyDate}</span>
          </div>
        </div>

        {/* Display Company Data */}
        <div className="flex items-center mt-2">
          <CompanyInfo company_data={company_data} />
          <div className="w-7/12">
            <img
              src={company_data.map_image || ""}
              alt="location_map"
              className="rounded-xl border-2 border-blue-400"
            />
          </div>
        </div>

        {/* Risk Scores */}
        <h3 className="mt-4 text-xl font-bold mb-4 text-blue-900">
          Risk Assessment
        </h3>
        
        {/* Overall Risk Score - Enhanced Display */}
        <OverallRiskGradeBar 
          grade={overallScoreGrade}
          score={overallScore}
        />

        {/* Location Risks */}
        <h3 className="mt-6 text-xl font-bold mb-4 text-blue-900">
          Location-Based Risks
        </h3>
        <div className="mb-4">
          <RiskGradeBar 
            grade={avgLocationRiskGrade}
            score={avgLocationRisk}
            label="Location Risk Average"
          />
        </div>
        <ul className="list-disc pl-5">
          {Object.entries(result.location_risk || {}).map(([risk, details]) => (
            <RiskItem
              key={risk}
              riskKey={risk}
              details={details}
              scoreData={locationScoresMap[risk]}
            />
          ))}
        </ul>

        {/* Industry-Specific Risks */}
        <h3 className="mt-6 text-xl font-bold mb-4 text-blue-900">
          Industry-Specific Risks
        </h3>
        <div className="mb-4">
          <RiskGradeBar 
            grade={avgIndustryRiskGrade}
            score={avgIndustryRisk}
            label="Industry Risk Average"
          />
        </div>
        <ul className="list-disc pl-5">
          {Object.entries(result.sector_specific_risk || {}).map(
            ([risk, details]) => (
              <RiskItem
                key={risk}
                riskKey={risk}
                details={details}
                scoreData={industryScoresMap[risk]}
              />
            )
          )}
        </ul>

        {/* Key Takeaways */}
        <h3 className="mt-6 text-xl font-bold mb-2 text-blue-900">
          Key Takeaways for Insurers
        </h3>
        <ul className="list-disc pl-5">
          {result.key_takeaways_for_insurers?.map((takeaway, index) => (
            <li key={index} className="text-gray-700">
              {takeaway}
            </li>
          )) || <p className="text-gray-500">No insights available.</p>}
        </ul>

        {/* Download Report */}
      </div>
      <button
        className="mt-6 px-6 py-4 bg-[rgb(71,63,125)] text-white rounded-[4px] text-[16px] font-['ArchivoExpandMid'] hover:bg-[rgb(71,63,125,0.8)] transition font-light"
        onClick={generatePDF}
      >
        Download Full Report (PDF)
      </button>
    </div>
  );
};

export default ResultsSection;
